import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * On Vigilante Collective product pages, the gallery keeps the product's
 * front/back image pairs in their original order and moves to the matching
 * front image when the primary visual option changes. Other product templates
 * retain Horizon's standard gallery replacement behavior.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.#isVigilanteCollectiveProduct()) {
      const picker = target?.querySelector('variant-picker, swatches-variant-picker-component');
      if (picker instanceof Element) {
        this.#lastVisualOption = this.#getPrimaryVisualOption(picker);
      }
    }
  }

  #controller = new AbortController();

  #lastVisualOption = '';

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event.
   *
   * Vigilante Collective merchandise keeps its current gallery markup so paired
   * views remain adjacent. All other products use Horizon's normal replacement.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    if (!this.#isVigilanteCollectiveProduct()) {
      event.promise
        .then(({ detail }) => {
          if (!detail?.html) return;

          const newMediaGallery = detail.html.querySelector('media-gallery');
          if (!newMediaGallery) return;

          this.replaceWith(newMediaGallery);
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
        });

      return;
    }

    event.promise
      .then(() => {
        const optionValue = this.#getPrimaryVisualOption(event.target);
        if (!optionValue || optionValue === this.#lastVisualOption) return;

        const slides = Array.from(this.querySelectorAll('slideshow-slide'));
        if (!slides.length) return;

        const selectedIndex = this.#findFrontSlideIndex(slides, optionValue);
        if (selectedIndex < 0) return;

        this.#lastVisualOption = optionValue;

        requestAnimationFrame(() => {
          this.slideshow?.select(selectedIndex, undefined, { animate: false });
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * @returns {boolean}
   */
  #isVigilanteCollectiveProduct() {
    const main = this.closest('#MainContent');
    return main instanceof HTMLElement && main.dataset.template === 'product.vigilante-collective';
  }

  /**
   * Reads the first product option in DOM order. For apparel and hats this is
   * Color; for mugs it is Size. Later garment-size dropdowns do not control
   * the image family.
   *
   * @param {Element} picker
   * @returns {string}
   */
  #getPrimaryVisualOption(picker) {
    const firstControl = picker.querySelector('select, fieldset');

    if (firstControl instanceof HTMLSelectElement) {
      return firstControl.value;
    }

    if (firstControl instanceof HTMLFieldSetElement) {
      const checked = firstControl.querySelector('input:checked');
      if (checked instanceof HTMLInputElement) return checked.value;
    }

    return '';
  }

  /**
   * Finds the front image belonging to the selected option. Product mockup
   * filenames include the option value and view, such as
   * "athletic-heather-front" or "40-oz-front".
   *
   * @param {Element[]} slides
   * @param {string} optionValue
   * @returns {number}
   */
  #findFrontSlideIndex(slides, optionValue) {
    const optionSlug = this.#slug(optionValue);

    let index = slides.findIndex((slide) => {
      const image = slide.querySelector('img');
      if (!(image instanceof HTMLImageElement)) return false;

      const path = this.#imagePath(image);
      return path.includes(optionSlug) && path.includes('front');
    });

    if (index >= 0) return index;

    index = slides.findIndex((slide) => {
      const image = slide.querySelector('img');
      if (!(image instanceof HTMLImageElement)) return false;

      return this.#imagePath(image).includes(optionSlug);
    });

    return index;
  }

  /**
   * @param {HTMLImageElement} image
   * @returns {string}
   */
  #imagePath(image) {
    const raw = image.currentSrc || image.src || '';

    try {
      return decodeURIComponent(new URL(raw, window.location.origin).pathname).toLowerCase();
    } catch {
      return raw.split('?')[0].toLowerCase();
    }
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #slug(value) {
    return value
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
